/**
 * Step 5 · Modelling & Estimation
 *
 * Density · Composite length · Block sizes · Search radius · Top-cut ·
 * Estimation method. All AI-suggested defaults are visible with confidence
 * and can be overridden manually. Locked/approved values are not touched
 * by subsequent AI applies.
 */
import { useEffect, useState } from "react";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { FieldRow, StepHeader } from "./Step1Identity";
import ConfigFieldRow from "../parts/ConfigFieldRow";

const NUMERIC = [
  { section: "modelling", field: "density_default",   label: "Bulk Density",         unit: "t/m³",   step: 0.01 },
  { section: "modelling", field: "composite_length",  label: "Composite Length",     unit: "m",       step: 0.1 },
  { section: "modelling", field: "block_size_x",      label: "Block Size X",         unit: "m",       step: 1 },
  { section: "modelling", field: "block_size_y",      label: "Block Size Y",         unit: "m",       step: 1 },
  { section: "modelling", field: "block_size_z",      label: "Block Size Z",         unit: "m",       step: 1 },
  { section: "modelling", field: "search_radius",     label: "Search Radius",        unit: "m",       step: 1 },
  { section: "modelling", field: "top_cut",           label: "Top-Cut",              unit: "grade",   step: 0.1 },
  { section: "modelling", field: "min_samples",       label: "Min Samples",           unit: "",         step: 1 },
  { section: "modelling", field: "max_samples",       label: "Max Samples",           unit: "",         step: 1 },
];

const METHODS = ["OrdinaryKriging", "InverseDistance", "NearestNeighbour", "IndicatorKriging", "SimpleKriging"];

export default function Step5Modelling({ cfg, patchCfg, aiApply, aiApplying, form, draftId }) {
  const model = cfg?.modelling || {};

  const applyAI = async () => {
    if (!draftId) return;
    await aiApply({});
  };

  return (
    <section className="space-y-4" data-testid="step-5-modelling">
      <StepHeader step="Step 5" title="Modelling & Estimation"
        subtitle="Working values for the block model. Every AI suggestion can be overridden. Locked values will never be silently overwritten by future AI runs." />

      <div className="flex items-center justify-end">
        <button type="button" onClick={applyAI} disabled={aiApplying || !draftId}
                    data-testid="btn-apply-ai-suggestions-modelling"
                    className="inline-flex items-center gap-1.5 rounded-md border border-brand-blue/30 bg-brand-blue/5 hover:bg-brand-blue/10 px-3 h-9 text-[12px] text-brand-blue font-medium disabled:opacity-50">
          {aiApplying ? <ArrowClockwise className="animate-spin" size={12} /> : <Sparkle size={12} weight="fill" />}
          Apply AI suggestions
        </button>
      </div>

      <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100">
        {NUMERIC.map((f) => (
          <ConfigFieldRow
            key={`${f.section}.${f.field}`}
            section={f.section}
            field={f.field}
            label={f.label}
            unit={f.unit}
            type="number"
            step={f.step}
            slot={model[f.field] || {}}
            onChange={(v) => patchCfg({ [`${f.section}.${f.field}`]: { value: v } })}
            onResetToAI={(ai) => patchCfg({ [`${f.section}.${f.field}`]: { value: ai } })}
          />
        ))}
        <ConfigFieldRow
          section="modelling"
          field="estimation_method"
          label="Estimation Method"
          type="select"
          options={METHODS}
          slot={model.estimation_method || {}}
          onChange={(v) => patchCfg({ ["modelling.estimation_method"]: { value: v } })}
          onResetToAI={(ai) => patchCfg({ ["modelling.estimation_method"]: { value: ai } })}
        />
      </div>
    </section>
  );
}
